"use client";

import { useQueryClient } from "@tanstack/react-query";
import { AppNames } from "@watt/common/libs/supabase/cookie-config";
import { queryKeysFactory } from "@watt/common/utils/query-keys-factory";
import { Button } from "@watt/ui/components/ui/button";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger
} from "@watt/ui/components/ui/collapsible";
import { ChevronDown } from "lucide-react";
import { useRouter } from "next/navigation";
import { useAction } from "next-safe-action/hooks";
import { useState } from "react";
import { toast } from "sonner";
import { quickSignIn } from "../../action";
import type { QuickLoginTestAccount } from "../../quick-login/accounts";

const VISIBLE_ACCOUNT_COUNT = 4;

type DevQuickLoginProps = {
  accounts: readonly QuickLoginTestAccount[];
};

export function DevQuickLogin({ accounts }: DevQuickLoginProps) {
  const router = useRouter();
  const queryClient = useQueryClient();
  const [isExpanded, setIsExpanded] = useState(false);

  const formAction = useAction(quickSignIn, {
    onError: ({ error }) => {
      toast.error("Quick sign-in failed", {
        description:
          error.validationErrors?._errors?.[0] ??
          "An error occurred during sign-in."
      });
    },
    onSuccess: result => {
      if (result?.data?.success && result?.data?.redirectTo) {
        queryClient.invalidateQueries({
          queryKey: queryKeysFactory.users.supabaseUser(AppNames.CRM)
        });
        router.replace(result.data.redirectTo);
      }
    }
  });

  const visibleAccounts = accounts.slice(0, VISIBLE_ACCOUNT_COUNT);
  const hiddenAccounts = accounts.slice(VISIBLE_ACCOUNT_COUNT);

  function renderAccountButton(account: QuickLoginTestAccount) {
    return (
      <Button
        className="w-full justify-between"
        disabled={formAction.isExecuting}
        key={account.email}
        onClick={() => {
          formAction.execute({ email: account.email });
        }}
        type="button"
        variant="outline"
      >
        <span>{account.label}</span>
        <span className="text-muted-foreground text-xs">{account.email}</span>
      </Button>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      {visibleAccounts.map(renderAccountButton)}
      {hiddenAccounts.length > 0 ? (
        <Collapsible onOpenChange={setIsExpanded} open={isExpanded}>
          <CollapsibleTrigger asChild>
            <Button
              className="w-full justify-between text-muted-foreground"
              type="button"
              variant="ghost"
            >
              <span>
                {isExpanded
                  ? "Hide more roles"
                  : `More roles (${hiddenAccounts.length})`}
              </span>
              <ChevronDown
                className={`h-4 w-4 transition-transform ${isExpanded ? "rotate-180" : ""}`}
              />
            </Button>
          </CollapsibleTrigger>
          <CollapsibleContent className="flex flex-col gap-2 pt-2">
            {hiddenAccounts.map(renderAccountButton)}
          </CollapsibleContent>
        </Collapsible>
      ) : null}
    </div>
  );
}
